import { useCallback, useRef, useState } from 'react';

// Past this many pixels, releasing dismisses rather than springs back. Roughly
// a thumb's travel: far enough that resting a finger on the handle and moving
// slightly does not close the sheet, short enough that a deliberate flick does
// not have to cross the whole screen.
const DISMISS_PX = 90;

// Pixels per millisecond. A fast flick dismisses whatever distance it covered,
// which is how a sheet can be thrown away in one short gesture. 0.5 is about
// the speed of a casual downward flick and well clear of a slow drag.
const DISMISS_VELOCITY = 0.5;

/**
 * Drag-to-dismiss for a sheet.
 *
 * Returns the live offset to add to the sheet's transform, whether a drag is
 * in progress (the caller suppresses its CSS transition while it is, or the
 * sheet lags the finger), and the handlers to spread onto the grab area.
 *
 * Deliberately not attached to the scrolling content. A sheet that dismisses
 * on any downward swipe cannot be scrolled back up, so the grab area is the
 * handle and header only, which is also where the affordance already is.
 *
 * `direction` is which way the sheet leaves. A sheet hanging from the top is
 * dismissed by pushing it back up, so it tracks upward travel instead. The
 * returned dragY is always positive, measured along that direction, and the
 * caller decides the sign of the transform.
 */
export function useSheetDrag(onDismiss, direction = 'down') {
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const start = useRef(null);

  const onTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    if (!touch) return;
    start.current = { y: touch.clientY, at: Date.now() };
    setDragging(true);
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!start.current) return;
    const touch = e.touches[0];
    if (!touch) return;
    // One direction only. Dragging a sheet past its own anchored edge would
    // expose the page behind it through the gap.
    const travel = direction === 'up'
      ? start.current.y - touch.clientY
      : touch.clientY - start.current.y;
    setDragY(Math.max(0, travel));
  }, [direction]);

  const onTouchEnd = useCallback(() => {
    if (!start.current) return;
    const elapsed = Date.now() - start.current.at;
    const velocity = dragY / Math.max(elapsed, 1);
    start.current = null;
    setDragging(false);

    // Reset before dismissing either way. On a dismiss the sheet animates out
    // from its resting transform, so leaving the drag offset in place would
    // make it jump back up by that amount first.
    setDragY(0);

    if (dragY > DISMISS_PX || (dragY > 16 && velocity > DISMISS_VELOCITY)) {
      onDismiss();
    }
  }, [dragY, onDismiss]);

  return {
    dragY,
    dragging,
    dragHandlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd },
  };
}
